import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowDownCircle, ArrowUpCircle, Filter, Calendar, Download, User, ChevronLeft, ChevronRight } from "lucide-react";

export default function BetboomPaymentsPreview() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const pageParam = searchParams.get('page');
  const [currentPage, setCurrentPage] = useState(pageParam ? parseInt(pageParam) || 1 : 1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showBrowser, setShowBrowser] = useState(true);
  const [browserTheme, setBrowserTheme] = useState('dark'); // 'dark' or 'light'
  const itemsPerPage = 10;

  useEffect(() => {
    const dataParam = searchParams.get('data');
    const browserParam = searchParams.get('showBrowser');
    
    if (browserParam !== null) {
      setShowBrowser(browserParam !== 'false');
    }
    
    if (dataParam) {
      try {
        const parsed = JSON.parse(decodeURIComponent(dataParam));
        setData(parsed);
        // Check showBrowser in data
        if (parsed.showBrowser !== undefined) {
          setShowBrowser(parsed.showBrowser);
        }
        // Check browserTheme in data
        if (parsed.browserTheme) {
          setBrowserTheme(parsed.browserTheme);
        }
        // Auto-set date filters from ALL transactions to show full history
        if (parsed.transactions && parsed.transactions.length > 0) {
          const allTxDates = parsed.transactions
            .map(t => {
              if (!t.date) return null;
              const parts = t.date.split('.');
              if (parts.length === 3) {
                return {
                  sortKey: parseInt(parts[2] + parts[1] + parts[0]),
                  isoDate: `${parts[2]}-${parts[1]}-${parts[0]}`
                };
              }
              return null;
            })
            .filter(d => d)
            .sort((a, b) => a.sortKey - b.sortKey);
          
          if (allTxDates.length > 0) {
            setDateFrom(allTxDates[0].isoDate);
            setDateTo(allTxDates[allTxDates.length - 1].isoDate);
          }
        }
      } catch (e) {
        console.error("Error parsing data:", e);
      }
    }
    
    // Read typeFilter from URL params or from data
    const typeP = searchParams.get('typeFilter');
    if (typeP && ['all', 'deposit', 'withdrawal'].includes(typeP)) {
      setTypeFilter(typeP);
    }
    
    // Read defaultTypeFilter from parsed data
    if (dataParam) {
      try {
        const pd = JSON.parse(decodeURIComponent(dataParam));
        if (pd.defaultTypeFilter && ['all', 'deposit', 'withdrawal'].includes(pd.defaultTypeFilter)) {
          setTypeFilter(pd.defaultTypeFilter);
        }
      } catch(e) {}
    }
  }, [searchParams]);

  // Reset to page 1 when filters change, but NOT when page is specified in URL
  useEffect(() => {
    if (searchParams.get('page')) {
      return; // page set from URL - never reset
    }
    setCurrentPage(1);
  }, [dateFrom, dateTo, typeFilter, searchParams]);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center">
        <div className="text-white">Загрузка...</div>
      </div>
    );
  }

  // Invert transaction types: OZON deposit -> template withdrawal, OZON withdrawal -> template deposit
  // This is because OZON "Пополнение" means user deposited TO OZON, which means withdrawal FROM betting
  // And OZON "Перевод" means user withdrew FROM OZON, which means deposit TO betting
  // BUT: First SBP deposit should NOT be inverted - it's a real deposit to betting account
  const invertedTransactions = (data.transactions || []).map(t => {
    // Don't invert the first SBP deposit
    if (t.isFirstDeposit) {
      return { ...t, isOzonTx: false };
    }
    return {
      ...t,
      type: t.type === 'deposit' ? 'withdrawal' : 'deposit',
      isOzonTx: true
    };
  });

  // Generate random transactions to mix with OZON transactions
  // NOTE: This is now DISABLED because mixing is done in BetboomTemplate.js
  // Keeping this function for backwards compatibility but not using it
  const generateRandomTransactions = (ozonTxs) => {
    return []; // Random transactions are already generated in BetboomTemplate.js
  };

  // Generate the mandatory first deposit transaction - NOT USED, already done in BetboomTemplate.js
  const generateFirstDeposit = (ozonTxs) => {
    return null; // First deposit is already added in BetboomTemplate.js
  };

  // Transactions already come pre-mixed from BetboomTemplate.js
  // No need to generate random ones here - just use what's in data
  const allTransactions = [...invertedTransactions];

  // Sort all transactions by date and time (newest first)
  let sortedTransactions = [...allTransactions].sort((a, b) => {
    // Parse date dd.mm.yyyy to comparable format
    const parseDate = (dateStr, timeStr) => {
      if (!dateStr) return 0;
      const parts = dateStr.split('.');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        const time = timeStr || '00:00:00';
        return new Date(`${year}-${month}-${day}T${time}`).getTime();
      }
      return 0;
    };
    
    const dateA = parseDate(a.date, a.time);
    const dateB = parseDate(b.date, b.time);
    return dateB - dateA; // Newest first
  });
  
  // Транзакции отсортированы по дате+времени, не переставляем

  // Parse date string "DD.MM.YYYY" to Date object for filtering
  const parseDateForFilter = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('.');
    if (parts.length === 3) {
      return new Date(parts[2], parts[1] - 1, parts[0]);
    }
    return null;
  };

  // Filter transactions by date range and type
  const filteredTransactions = sortedTransactions.filter(tx => {
    // Type filter
    if (typeFilter !== 'all') {
      if (typeFilter === 'deposit' && tx.type !== 'deposit') return false;
      if (typeFilter === 'withdrawal' && tx.type !== 'withdrawal') return false;
    }
    
    // Date filter
    if (!dateFrom && !dateTo) return true;
    
    const txDate = parseDateForFilter(tx.date);
    if (!txDate) return true;
    
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      if (txDate < fromDate) return false;
    }
    
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (txDate > toDate) return false;
    }
    
    return true;
  });

  // Pagination - use filtered transactions
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTransactions = filteredTransactions.slice(startIndex, endIndex);

  // Calculate totals from all transactions
  const deposits = sortedTransactions.filter(t => t.type === 'deposit');
  const withdrawals = sortedTransactions.filter(t => t.type === 'withdrawal');
  const totalDeposits = deposits.reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalWithdrawals = withdrawals.reduce((sum, t) => sum + (t.amount || 0), 0);
  const balance = data.balance || 0;
  
  // Format name for display
  const displayName = data.firstName ? `${data.firstName} ${data.lastName?.[0]?.toUpperCase() || ''}.` : 'Пользователь';

  // Determine payment method based on OZON category
  const getPaymentMethod = (transaction) => {
    // If transaction has explicit method and is not an OZON transaction, use it directly
    if (transaction.method && !transaction.isOzonTx) {
      return transaction.method;
    }
    // Map OZON categories to Betboom payment methods
    const category = (transaction.category || '').toLowerCase();
    if (category.includes('финанс')) return 'Карта';
    if (category.includes('пополнен')) return 'СБП';
    if (category.includes('перевод')) return 'СБП';
    if (['супермаркет', 'фастфуд', 'маркетплейс', 'развлечен', 'магазин', 'одежда', 'электроник', 'аптек', 'топливо', 'транспорт', 'связь', 'отел'].some(x => category.includes(x))) return 'Карта';
    // Default: deposits = СБП, withdrawals = Карта
    return transaction.type === 'deposit' ? 'СБП' : 'Карта';
  };

  // Generate transaction ID
  const generateTxId = (transaction, index) => {
    const date = transaction.date?.replace(/\./g, '') || '20250127';
    const time = (transaction.time || '00:00:00').replace(/:/g, '').slice(0, 6);
    return `TR${date}${time}${String(index + 1).padStart(3, '0')}`;
  };

  // Format date for display - Russian format DD.MM.YYYY HH:MM (Moscow time)
  const formatDateTime = (date, time) => {
    const d = date || new Date().toLocaleDateString('ru-RU');
    // Remove seconds from time if present (HH:MM:SS -> HH:MM)
    let t = time || '12:00';
    if (t.split(':').length > 2) {
      t = t.split(':').slice(0, 2).join(':');
    }
    // Keep dd.mm.yyyy format for Russian locale
    return `${d} ${t}`;
  };

  // Browser Frame Component - supports light and dark themes
  const BrowserFrame = ({ theme = 'dark' }) => {
    const isLight = theme === 'light';
    const bgColor = isLight ? '#dee1e6' : '#202124';
    const tabBgActive = isLight ? '#ffffff' : '#0f0f10';
    const tabBgInactive = isLight ? '#c8ccd1' : '#35363a';
    const textActive = isLight ? '#202124' : '#e8eaed';
    const textInactive = isLight ? '#5f6368' : '#9aa0a6';
    const toolbarBg = isLight ? '#f1f3f4' : '#292a2d';
    const urlBarBg = isLight ? '#ffffff' : '#202124';
    const urlBarBorder = isLight ? '#dadce0' : '#3c4043';
    
    return (
      <div className="flex-shrink-0" style={{ backgroundColor: bgColor }}>
        <div className="flex items-center h-[38px]" style={{ backgroundColor: bgColor }}>
          <div className="flex items-end flex-1 pl-2">
            {/* Tab 1 - Ozon Карта */}
            <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] cursor-pointer" style={{ backgroundColor: tabBgInactive }}>
              <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="text-[13px] truncate flex-1" style={{ color: textInactive }}>Ozon Карта</span>
            </div>
            {/* Tab 2 - BetBoom (ACTIVE) */}
            <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] cursor-pointer" style={{ backgroundColor: tabBgActive }}>
              <img src="/betboom-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
              <span className="text-[13px] truncate flex-1" style={{ color: textActive }}>BetBoom</span>
            </div>
            <button className="w-7 h-7 flex items-center justify-center rounded-full ml-1 mb-1" style={{ color: textInactive }}>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
            </button>
          </div>
          <div className="flex items-center h-full">
            <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: textInactive }}><svg className="w-[10px] h-[1px]" viewBox="0 0 10 1"><rect fill="currentColor" width="10" height="1"/></svg></button>
            <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: textInactive }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1"><rect x="0.5" y="0.5" width="9" height="9"/></svg></button>
            <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#e81123] hover:text-white" style={{ color: textInactive }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.2"><line x1="0" y1="0" x2="10" y2="10"/><line x1="10" y1="0" x2="0" y2="10"/></svg></button>
          </div>
        </div>
        <div className="flex items-center h-[40px] px-2" style={{ backgroundColor: toolbarBg }}>
          <div className="flex items-center gap-1 mr-2">
            <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textInactive }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg></button>
            <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: isLight ? '#c0c4c8' : '#5f6368' }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg></button>
            <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textInactive }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg></button>
          </div>
          <div className="flex-1 flex items-center h-[32px] px-4 rounded-full" style={{ backgroundColor: urlBarBg, border: `1px solid ${urlBarBorder}` }}>
            <svg className="w-4 h-4 mr-3 flex-shrink-0" style={{ color: textInactive }} viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
            <span className="text-[14px]" style={{ color: textActive }}>betboom.ru/payments</span>
          </div>
          <div className="flex items-center gap-1 ml-3">
            <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textInactive }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></button>
            <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textInactive }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="2"/><circle cx="12" cy="5" r="2"/><circle cx="12" cy="19" r="2"/></svg></button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Global styles to ensure dark background fills entire page */}
      <style>{`
        html, body, #root {
          background-color: #0f0f10 !important;
          min-height: 100vh;
          margin: 0;
          padding: 0;
        }
      `}</style>
      <div className="flex flex-col template-preview-scale" style={{ backgroundColor: '#0f0f10', minHeight: '100vh', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
        {showBrowser && <BrowserFrame theme={browserTheme} />}
        
        <div className="flex-1 bg-[#0f0f10] text-white" style={{ minHeight: showBrowser ? 'auto' : '100vh' }}>
      {/* Header - Same as Profile Page */}
      <header className="bg-[#0d0d0d] border-b border-gray-800">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Nav */}
            <div className="flex items-center gap-8">
              <span className="text-white font-bold text-xl tracking-tight">BetBoom</span>
              <nav className="hidden md:flex items-center gap-6">
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Линия</a>
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Киберспорт</a>
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Спорт 24/7</a>
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Акции</a>
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Клубы</a>
              </nav>
            </div>
            
            {/* Balance & Profile */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-[#f9d547] font-bold text-lg">{balance.toLocaleString()}₽</span>
                <span className="text-gray-500 text-xs">Игра на деньги</span>
              </div>
              <div className="flex items-center gap-3">
                <button className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </button>
                <div className="w-9 h-9 bg-[#1a1a1a] rounded-full flex items-center justify-center border border-gray-700">
                  <User className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">История платежей</h1>
          <p className="text-gray-400">Просмотр всех ваших транзакций и операций</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-[#1a1a1a] rounded-lg p-6 border border-gray-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Пополнения</span>
              <ArrowDownCircle className="w-5 h-5 text-green-500" />
            </div>
            <div className="text-2xl font-bold">{totalDeposits.toLocaleString()}&nbsp;₽</div>
          </div>
          <div className="bg-[#1a1a1a] rounded-lg p-6 border border-gray-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Выводы</span>
              <ArrowUpCircle className="w-5 h-5 text-red-500" />
            </div>
            <div className="text-2xl font-bold">{totalWithdrawals.toLocaleString()}&nbsp;₽</div>
          </div>
          <div className="bg-[#1a1a1a] rounded-lg p-6 border border-gray-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Баланс</span>
            </div>
            <div className="text-2xl font-bold text-[#f9d547]">{balance.toLocaleString()}&nbsp;₽</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[#1a1a1a] rounded-lg p-6 border border-gray-800 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-gray-400" />
            <h2 className="text-lg font-semibold">Фильтры</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Тип операции</label>
              <select 
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full bg-[#0f0f10] border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-[#f9d547] transition-colors"
              >
                <option value="all">Все операции</option>
                <option value="deposit">Пополнения</option>
                <option value="withdrawal">Выводы</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">С даты</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input 
                  type="date"
                  value={dateFrom || ''}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full bg-[#0f0f10] border border-gray-700 rounded pl-10 pr-4 py-2 text-white focus:outline-none focus:border-[#f9d547] transition-colors cursor-pointer"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">По дату</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input 
                  type="date"
                  value={dateTo || ''}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full bg-[#0f0f10] border border-gray-700 rounded pl-10 pr-4 py-2 text-white focus:outline-none focus:border-[#f9d547] transition-colors cursor-pointer"
                />
              </div>
            </div>
            <div className="flex items-end">
              <button 
                onClick={() => { setDateFrom(''); setDateTo(''); setTypeFilter('all'); }}
                className="w-full bg-[#2a2a2a] hover:bg-[#333] text-white font-medium px-4 py-2 rounded transition-colors"
              >
                Сбросить
              </button>
            </div>
            <div className="flex items-end">
              <button className="w-full bg-[#f9d547] hover:bg-[#f5cd2e] text-black font-medium px-4 py-2 rounded transition-colors flex items-center justify-center gap-2">
                <Download className="w-4 h-4" />
                Экспорт
              </button>
            </div>
          </div>
          {/* Active filters info */}
          {(dateFrom || dateTo || typeFilter !== 'all') && (
            <div className="mt-4 text-sm text-gray-400">
              Найдено операций: {filteredTransactions.length} из {sortedTransactions.length}
            </div>
          )}
        </div>

        {/* Transactions Table */}
        <div className="bg-[#1a1a1a] rounded-lg border border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#0f0f10] border-b border-gray-800">
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-400">Дата и время</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-400">Тип</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-400">Метод</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-400">ID транзакции</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-gray-400">Сумма</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-gray-400">Статус</th>
                </tr>
              </thead>
              <tbody>
                {currentTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-400">
                      {(dateFrom || dateTo || typeFilter !== 'all') ? 'Нет транзакций за выбранный период' : 'Нет транзакций'}
                    </td>
                  </tr>
                ) : (
                  currentTransactions.map((tx, index) => {
                    const isDeposit = tx.type === 'deposit';
                    const method = getPaymentMethod(tx);
                    const txId = generateTxId(tx, startIndex + index);
                    const dateTime = formatDateTime(tx.date, tx.time);
                    
                    return (
                      <tr key={index} className="border-b border-gray-800 hover:bg-[#212121] transition-colors">
                        <td className="px-6 py-4 text-sm text-gray-300">{dateTime}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {isDeposit ? (
                              <ArrowDownCircle className="w-4 h-4 text-green-500" />
                            ) : (
                              <ArrowUpCircle className="w-4 h-4 text-red-500" />
                            )}
                            <span className="text-sm">{isDeposit ? 'Пополнение' : 'Вывод'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-300">{method}</td>
                        <td className="px-6 py-4 text-sm text-gray-400 font-mono">{txId}</td>
                        <td className="px-6 py-4 text-right">
                          <span className={`text-sm font-semibold ${isDeposit ? 'text-green-500' : 'text-red-500'}`}>
                            {isDeposit ? '+' : '-'}{tx.amount?.toLocaleString()}&nbsp;₽
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-sm font-medium text-green-500">Выполнено</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Показано {startIndex + 1}-{Math.min(endIndex, sortedTransactions.length)} из {sortedTransactions.length} транзакций
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`flex items-center gap-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                currentPage === 1 
                  ? 'bg-gray-800 text-gray-600 cursor-not-allowed' 
                  : 'bg-[#1a1a1a] text-white hover:bg-[#252525] border border-gray-700'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              Назад
            </button>
            
            {/* Page numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-10 h-10 rounded text-sm font-medium transition-colors ${
                    currentPage === page
                      ? 'bg-[#f9d547] text-black'
                      : 'bg-[#1a1a1a] text-white hover:bg-[#252525] border border-gray-700'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`flex items-center gap-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                currentPage === totalPages 
                  ? 'bg-gray-800 text-gray-600 cursor-not-allowed' 
                  : 'bg-[#1a1a1a] text-white hover:bg-[#252525] border border-gray-700'
              }`}
            >
              Вперёд
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
    </>
  );
}
